/**
 * Username share-link verifier.
 *
 * Web port of `kaether_for_ios/Stoat/E2EE/UsernameShareLinkVerifier.swift`.
 *
 * Once the receiver has decoded a `UsernameShareLink` from the link and
 * fetched the corresponding claim from the server, this pure-function
 * verifier decides whether to trust the binding.
 *
 * The order matters for the threat model:
 *   1. Username equality is a structural check.
 *   2. Fingerprint match is the cheap cryptographic check (one SHA-256)
 *      that catches the most damaging server attack: silently rebinding
 *      "alice.0000" to a key the server controls. We do this BEFORE the
 *      signature verification because it costs less and fails faster on
 *      attacker-supplied input. See `testFingerprintCheckedBeforeSignature`.
 *   3. Signature verification is the most expensive step and only runs
 *      after the fingerprint matches.
 */

import {
  type UsernameShareLink,
  type UsernameHandle,
  formatHandle,
  computeFingerprint,
  FINGERPRINT_BYTE_COUNT,
} from "./UsernameShareLink";

// ---------------------------------------------------------------------------
// Verification result
// ---------------------------------------------------------------------------

export type UsernameShareLinkVerificationResult =
  | { readonly tag: "ok" }
  | {
      readonly tag: "usernameMismatch";
      readonly linkHandle: UsernameHandle;
      readonly claimHandle: UsernameHandle;
    }
  | { readonly tag: "fingerprintMismatch" }
  | { readonly tag: "claimSignatureInvalid" };

// ---------------------------------------------------------------------------
// Fetched-claim shape (server-supplied, not yet trusted)
// ---------------------------------------------------------------------------

/**
 * Minimal claim surface the verifier needs. Callers supply whatever the
 * server returns; this interface captures the load-bearing fields.
 *
 * `identityPublicKey` — raw bytes of the Ed25519 public key (32 bytes for
 *   Curve25519 keys, arbitrary length for other key types in future schemas).
 * `signature` — Ed25519 signature over the canonical bytes of the payload.
 * `verifySignature` — caller-supplied predicate that verifies the signature;
 *   this keeps the verifier agnostic to the exact signing algorithm and
 *   SubtleCrypto key-import ceremony.
 */
export interface FetchedUsernameClaim {
  readonly handle: UsernameHandle;
  readonly identityPublicKey: Uint8Array;
  /** Verify that the claim's signature covers the canonical payload bytes. */
  readonly verifySignature: () => Promise<boolean>;
}

// ---------------------------------------------------------------------------
// verify
// ---------------------------------------------------------------------------

/**
 * Compare the link's promises against the server-supplied claim.
 * Returns the first failure encountered or `{ tag: "ok" }` if every
 * check passes.
 *
 * Ordering (load-bearing — mirrors iOS exactly):
 *   1. `claim.handle == link.handle` — else `usernameMismatch`
 *   2. `SHA256(claim.identityPublicKey).prefix(16) == link.fingerprint` — else `fingerprintMismatch`
 *   3. `claim.verifySignature()` — else `claimSignatureInvalid`
 *   4. `ok`
 *
 * Fingerprint check fires before signature check so a hostile server's
 * most effective attack (substitute the identity key) is caught by the
 * cheap hash comparison rather than the more expensive Ed25519 verify.
 */
export async function verify(
  link: UsernameShareLink,
  claim: FetchedUsernameClaim,
): Promise<UsernameShareLinkVerificationResult> {
  // Step 1: username equality
  if (
    claim.handle.canonicalName !== link.handle.canonicalName ||
    claim.handle.discriminator !== link.handle.discriminator
  ) {
    return {
      tag: "usernameMismatch",
      linkHandle: link.handle,
      claimHandle: claim.handle,
    };
  }

  // Step 2: fingerprint match — cheap SHA-256 before Ed25519 verify
  const derivedFingerprint = await computeFingerprint(claim.identityPublicKey);
  if (derivedFingerprint.length !== FINGERPRINT_BYTE_COUNT) {
    return { tag: "fingerprintMismatch" };
  }
  for (let i = 0; i < FINGERPRINT_BYTE_COUNT; i++) {
    if (derivedFingerprint[i] !== link.fingerprint[i]) {
      return { tag: "fingerprintMismatch" };
    }
  }

  // Step 3: signature verification — only after fingerprint agrees
  const sigOk = await claim.verifySignature();
  if (!sigOk) {
    return { tag: "claimSignatureInvalid" };
  }

  return { tag: "ok" };
}

/**
 * Formats a `UsernameShareLinkVerificationResult` for display /
 * diagnostic use. Not load-bearing.
 */
export function describeVerificationResult(
  r: UsernameShareLinkVerificationResult,
): string {
  switch (r.tag) {
    case "ok":
      return "ok";
    case "usernameMismatch":
      return `usernameMismatch: link=${formatHandle(r.linkHandle)} claim=${formatHandle(r.claimHandle)}`;
    case "fingerprintMismatch":
      return "fingerprintMismatch";
    case "claimSignatureInvalid":
      return "claimSignatureInvalid";
  }
}
